/**
 * Helpers for the factsheet HTML editor.
 *
 * GET /bac/factsheet/<file_id>/html/ returns a WHOLE HTML document - doctype,
 * <head> with the stylesheet the PDF renderer lays the sheet out with, and the
 * <body>. Only the body may be edited: the CSS in the head is what makes the
 * regenerated PDF look like the original, so it is peeled off, kept verbatim and
 * re-attached on save. That is the opposite of the notice editor, which
 * allowlists markup down to what the .docx converter understands - here the
 * markup (classes, inline styles, table structure) IS the layout, so nothing is
 * filtered out beyond what is unsafe to render.
 */

// Marks where the edited body goes back into the preserved document shell.
const BODY_PLACEHOLDER = "<!--ACCB_FACTSHEET_BODY-->";

const FULL_DOCUMENT = /<(!doctype|html|body)[\s>]/i;

/** True when the backend sent a whole document rather than a bare fragment. */
export const isFullHtmlDocument = (content) =>
  FULL_DOCUMENT.test(String(content || ""));

/* ------------------------------------------------------------------ *
 * Document split / join
 * ------------------------------------------------------------------ */

/**
 * Splits the factsheet into the parts TinyMCE must not see and the body it edits.
 *
 * @param {string} content the `content` field of the factsheet response
 * @returns {{ shell: string, bodyHtml: string, css: string }}
 *   `shell` is the document with BODY_PLACEHOLDER standing in for the body,
 *   `css` is every <style> block's text, fed to the editor so it renders the
 *   sheet the way the PDF does.
 */
export const splitFactsheetDocument = (content) => {
  const raw = String(content || "");
  const fragmentOnly = { shell: BODY_PLACEHOLDER, bodyHtml: raw, css: "" };

  let doc;
  try {
    doc = new DOMParser().parseFromString(raw, "text/html");
  } catch (err) {
    return fragmentOnly;
  }
  if (!doc || !doc.body) return fragmentOnly;

  // TinyMCE drops <style> blocks that sit in the body, which would strip the
  // sheet's own CSS out of the saved document. Hoist them into the head, which
  // is preserved verbatim, before the body is handed over.
  Array.from(doc.body.querySelectorAll("style")).forEach((node) =>
    doc.head.appendChild(node)
  );

  const styleNodes = Array.from(doc.head.querySelectorAll("style"));
  const css = styleNodes.map((node) => node.textContent || "").join("\n");
  const bodyHtml = doc.body.innerHTML;

  // A fragment is rebuilt as a fragment: wrapping it in html/head/body would
  // hand the renderer a different document from the one it gave us.
  if (!isFullHtmlDocument(raw)) {
    const styleMarkup = styleNodes.map((node) => node.outerHTML).join("");
    return { shell: `${styleMarkup}${BODY_PLACEHOLDER}`, bodyHtml, css };
  }

  doc.body.innerHTML = BODY_PLACEHOLDER;
  const doctype = doc.doctype ? `<!DOCTYPE ${doc.doctype.name}>` : "";
  return {
    shell: `${doctype}${doc.documentElement.outerHTML}`,
    bodyHtml,
    css,
  };
};

/**
 * Puts the edited body back into the preserved shell.
 *
 * The replacement is a function on purpose: `$&`, `$'` and friends in the edited
 * HTML are substitution patterns to String.replace, so a literal string here
 * would silently corrupt any factsheet containing them.
 */
export const joinFactsheetDocument = (shell, bodyHtml) => {
  const body = bodyHtml || "";
  if (!shell) return body;
  if (!shell.includes(BODY_PLACEHOLDER)) return body;
  return shell.replace(BODY_PLACEHOLDER, () => body);
};

/* ------------------------------------------------------------------ *
 * Safety pass
 * ------------------------------------------------------------------ */

// Removed outright: their content is executable or navigational, never wording,
// so unwrapping them the way a text element would be unwrapped makes no sense.
// Everything else - classes, inline styles, tables, spans - is layout the PDF
// renderer depends on and is left exactly as it was.
const UNSAFE_TAGS = "script, iframe, object, embed, base, link[rel~='import']";

const SAFE_URL = /^(https?:|mailto:|tel:|#|\/|data:image\/)/i;

/**
 * Strips what must not be rendered or round-tripped, and nothing else.
 * @param {string} html
 * @returns {string}
 */
export const stripUnsafeFactsheetHtml = (html) => {
  if (!html || typeof html !== "string") return "";

  let doc;
  try {
    doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  } catch (err) {
    return "";
  }

  const root = doc.body.firstElementChild;
  if (!root) return "";

  root.querySelectorAll(UNSAFE_TAGS).forEach((node) => node.remove());

  root.querySelectorAll("*").forEach((node) => {
    Array.from(node.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on")) {
        node.removeAttribute(attr.name);
        return;
      }
      if ((name === "href" || name === "src") && !SAFE_URL.test(attr.value.trim())) {
        node.removeAttribute(attr.name);
      }
    });
  });

  return root.innerHTML;
};

/* ------------------------------------------------------------------ *
 * Editor configuration
 * ------------------------------------------------------------------ */

// Only enough to give the editor iframe a page to sit on. It is emitted BEFORE
// the factsheet's own CSS so any rule the document declares for these same
// properties wins.
const FACTSHEET_BASE_CSS = `
  body {
    background: #fff;
    color: #000;
    margin: 0;
    padding: 0.5in 0.6in;
  }
  table { border-collapse: collapse; }
`;

/**
 * Rewrite the factsheet's own stylesheet so it only applies inside the editor.
 *
 * TinyMCE rendered the body in an iframe, so `content_style` was isolated by
 * construction - the sheet's rules could say `body { padding: 0.5in }` or
 * `.label { font-weight: bold }` and touch nothing else. Tiptap renders into
 * the page, so the same CSS would restyle the whole application. Every rule is
 * therefore prefixed with the editor's own selector before being injected.
 *
 * `body` and `html` selectors become the scope itself, since inside the iframe
 * that is what they referred to. At-rules that cannot be scoped (@font-face,
 * @page, @import) are passed through untouched; @media and @supports are
 * recursed into.
 *
 * @param {string} css   the factsheet's stylesheet, from splitFactsheetDocument
 * @param {string} scope the selector to confine it to
 * @returns {string} CSS
 */
export const scopeFactsheetCss = (css, scope) => {
  const source = String(css || "");
  if (!source.trim() || !scope) return "";

  const prefixSelector = (selector) =>
    selector
      .split(",")
      .map((part) => {
        const trimmed = part.trim();
        if (!trimmed) return "";
        if (/^(body|html)$/i.test(trimmed)) return scope;
        if (/^(body|html)\b/i.test(trimmed)) {
          return `${scope}${trimmed.replace(/^(body|html)/i, "")}`;
        }
        return `${scope} ${trimmed}`;
      })
      .filter(Boolean)
      .join(", ");

  // Walk the stylesheet tracking brace depth, so nested at-rules stay intact.
  const scopeBlock = (text) => {
    let out = "";
    let buffer = "";
    let index = 0;

    while (index < text.length) {
      const char = text[index];

      if (char !== "{") {
        buffer += char;
        index += 1;
        continue;
      }

      // Found a block: pair the braces.
      let depth = 1;
      let bodyStart = index + 1;
      let cursor = bodyStart;
      while (cursor < text.length && depth > 0) {
        if (text[cursor] === "{") depth += 1;
        else if (text[cursor] === "}") depth -= 1;
        cursor += 1;
      }

      const prelude = buffer.trim();
      const body = text.slice(bodyStart, cursor - 1);
      buffer = "";
      index = cursor;

      if (prelude.charAt(0) === "@") {
        if (/^@(media|supports|document|layer)\b/i.test(prelude)) {
          out += `${prelude} { ${scopeBlock(body)} }
`;
        } else {
          // @font-face, @keyframes, @page - not scopable, keep verbatim.
          out += `${prelude} { ${body} }
`;
        }
      } else if (prelude) {
        out += `${prefixSelector(prelude)} { ${body} }
`;
      }
    }

    return out;
  };

  return scopeBlock(source);
};

/**
 * @param {string} css the factsheet's own stylesheet, from splitFactsheetDocument
 */
export const buildFactsheetEditorInit = (css = "") => ({
  // A floor: FactsheetEditor stretches the editor to fill the dialog.
  height: 560,
  menubar: false,
  statusbar: false,
  branding: false,
  plugins: ["lists", "advlist", "wordcount", "searchreplace", "table", "paste", "autolink", "link", "code"],
  toolbar:
    "undo redo | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent | link | table | removeformat | code",
  elementpath: false,
  forced_root_block: "p",
  // The factsheet is a generated document: every class and inline style on it is
  // read by the PDF renderer, so nothing is filtered on the way in or out.
  // stripUnsafeFactsheetHtml is what keeps this honest.
  valid_elements: "*[*]",
  extended_valid_elements: "*[*]",
  valid_children: "+body[style]",
  // Keep &nbsp; and other entities as the document wrote them.
  entity_encoding: "raw",
  // The sheet's spacing comes from its own CSS; re-encoding it would shift it.
  paste_webkit_styles: "all",
  content_style: `${FACTSHEET_BASE_CSS}\n${css || ""}`,
});

/** Turns `front_sheet` into `Front Sheet` for the dialog heading. */
export const humanizeFactsheetType = (type) =>
  String(type || "")
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
